import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';

type NavItem = {
  label: string;
  to: string;
};

const toolsNavItems: NavItem[] = [
  { label: 'Hotel Explorer', to: '/tools/hotel-explorer' },
  { label: 'Trace Viewer', to: '/tools/trace-viewer' },
  { label: 'Changelog Viewer', to: '/tools/changelog' },
  { label: 'Enqueue Center', to: '/tools/enqueue-center' },
  { label: 'Reports', to: '/tools/reports' },
  { label: 'Companies', to: '/tools/companies' },
  { label: 'System Values', to: '/tools/system-values' },
  { label: 'Booking Engines', to: '/tools/booking-engines' },
  { label: 'Data Requests', to: '/tools/data-requests' },
  { label: 'Entity Explorer', to: '/tools/entity-explorer' },
];

export function HomePage(): ReactElement {
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const toolsMenuRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    setIsToolsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isToolsOpen) {
      return;
    }

    function handlePointerDown(event: MouseEvent): void {
      if (!toolsMenuRef.current?.contains(event.target as Node)) {
        setIsToolsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isToolsOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-white to-sky-50 text-slate-900">
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-none items-center justify-between px-4 sm:px-6 lg:px-8">
          <NavLink
            to="/"
            end
            className="ml-6 rounded-md text-lg font-semibold tracking-tight text-slate-900 transition hover:text-sky-700"
          >
            Admin App
          </NavLink>

          <div className="relative" ref={toolsMenuRef}>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-900"
              onClick={() => setIsToolsOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={isToolsOpen}
            >
              Tools
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.12l3.71-3.9a.75.75 0 0 1 1.08 1.04l-4.25 4.47a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {isToolsOpen ? (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-48 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
              >
                {toolsNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    className={({ isActive }) =>
                      `block rounded-md px-3 py-2 text-sm transition ${
                        isActive
                          ? 'bg-sky-100 text-sky-800'
                          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-none px-4 pb-10 pt-24 sm:px-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  );
}
